import { FC, useState, useEffect, useRef } from "react";
import { Box } from "@radix-ui/themes";
import useViewportWidth from "@/hooks/useViewportWidth";
import { CoverCarouselProps } from "./CoverCarousel.types";
import { CarouselSlide } from "./CarouselSlide";
import { CarouselContent } from "./CarouselContent";
import { CircularProgress } from "./CircularProgress";
import { SlideIndicators } from "./SlideIndicators";

/**
 * A React component that displays a cover carousel.
 *
 * Used on the Zones overview pages.
 *
 * @prop {SlideType[]} slides - An array of slides to display in the carousel
 * @prop {number} interval - (Optional) Time between slide transitions in milliseconds, defaults to 5000ms
 * @prop {string} logoURL - (Optional) The URL of the logo to display on the slide
 * @prop {string} coverURL - (Optional) The URL of the cover image
 * @prop {string} alt - (Optional) The alt text of the cover image
 * @prop {string} heading - (Optional) The heading of the slide
 * @prop {string} desc - (Optional) The description of the slide
 * @prop {string} theme - (Optional) The theme of the slide
 * @prop {Link[]} links - (Optional) An array of links to display on the slide
 */
const CoverCarousel: FC<CoverCarouselProps> = ({
  slides,
  interval = 5000,
}): JSX.Element => {
  const isMobile = useViewportWidth(768);
  const isTablet = useViewportWidth(1280);
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const hasSeveralSlides = slides.length > 1;

  // Auto-advance slides if there are multiple
  useEffect(() => {
    if (!hasSeveralSlides) return;

    let startTime: number;
    let animationFrameId: number;

    const updateProgress = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const newProgress = Math.min((elapsed / interval) * 100, 100);
      setProgress(newProgress);

      if (elapsed < interval) {
        animationFrameId = requestAnimationFrame(updateProgress);
      }
    };

    // Start progress animation
    animationFrameId = requestAnimationFrame(updateProgress);

    // Set timer for slide transition
    const timer = setInterval(() => {
      setPrevIndex(activeIndex);
      setIsTransitioning(true);
      setActiveIndex((current) => (current + 1) % slides.length);
      setProgress(0); // Reset progress
      startTime = 0; // Reset start time

      // Reset transition state after animation completes
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    }, interval);

    return () => {
      clearInterval(timer);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [hasSeveralSlides, slides.length, interval, activeIndex]);

  // Function to manually change slides
  const goToSlide = (index: number) => {
    if (index === activeIndex) return;
    setPrevIndex(activeIndex);
    setIsTransitioning(true);
    setActiveIndex(index);
    setProgress(0); // Reset progress when manually changing slides

    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  const activeSlide = slides[activeIndex];
  const prevSlide = slides[prevIndex];

  return (
    <Box
      className="cover-carousel align-full"
      position="relative"
      style={{ overflow: "hidden" }}
    >
      <CarouselSlide
        activeSlide={activeSlide}
        prevSlide={prevSlide}
        isTransitioning={isTransitioning}
        isMobile={isMobile}
        isTablet={isTablet}
      />

      <CarouselContent activeSlide={activeSlide} />

      {hasSeveralSlides && (
        <>
          <CircularProgress
            activeIndex={activeIndex}
            totalSlides={slides.length}
            progress={progress}
          />

          <SlideIndicators
            slides={slides}
            activeIndex={activeIndex}
            goToSlide={goToSlide}
          />
        </>
      )}
    </Box>
  );
};

export default CoverCarousel;
